/**
 * Answering a form, and signing it.
 *
 * Rendered from the immutable version the assignment names, so what the
 * person answers is exactly what the record will say they answered. Each
 * field is checked before sending, with the same rules the server applies
 * and the ones only a person can satisfy (tick the acknowledgement, type
 * a full name).
 */

import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { isCallFailure } from '../../lib/callable';
import { DESCRIPTION_MAX } from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { FileDrop } from '../../shared/FileDrop';
import { formatBytes } from '../policies/useUpload';
import { formatDay } from '../../shared/dates';
import { useFormVersion, useMyFormAssignments, useSubmitAttestation } from './useForms';
import { answerFileProblem, ANSWER_ACCEPT, uploadAnswerFile } from './uploadAnswer';
import { answerProblem, assignmentActions, type AnswerValue, type FieldDef } from './types';
import { SUBMITTED_KEY } from './MyFormsPage';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Forms.module.css';

type Answers = Record<string, AnswerValue | undefined>;

export function CompletePage() {
  const { assignmentId = '' } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const roles = session?.roles ?? [];

  const mine = useMyFormAssignments(orgId, uid, true);
  const assignment = (mine.data ?? []).find((a) => a.id === assignmentId) ?? null;
  const version = useFormVersion(orgId, assignment?.formId ?? null, assignment?.formVersionId ?? null);
  const submit = useSubmitAttestation(orgId, uid);

  const [answers, setAnswers] = useState<Answers>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [attested, setAttested] = useState(false);
  /** The day shown on the attestation line, read once rather than on every render. */
  const [today] = useState(() => new Date().toISOString());
  const [touched, setTouched] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  if (mine.isLoading || (assignment && version.isLoading)) return <Spinner label="Opening the form" />;
  if (!assignment) {
    return <div className={styles.body}><PageState title="That form is not yours, or is not here" action={{ to: '/forms/mine', label: 'Back to your forms' }} tone="alert">Only the person it was assigned to can complete it.</PageState></div>;
  }
  const can = assignmentActions(assignment, roles, uid);
  if (!can.complete) {
    return <div className={styles.body}><PageState title="Nothing to complete" action={{ to: '/forms/mine', label: 'Back to your forms' }}>This assignment is {assignment.status}.</PageState></div>;
  }
  if (version.error || !version.data) {
    return <div className={styles.body}><PageState title="Could not open the form" action={{ to: '/forms/mine', label: 'Back to your forms' }} tone="alert">{isCallFailure(version.error) ? version.error.message : 'Something went wrong.'}</PageState></div>;
  }

  const v = version.data;
  const problems = new Map(v.fields.map((fd) => [fd.id, fd.type === 'fileUpload' ? (fd.required && !files[fd.id] && !answers[fd.id] ? 'Attach a file.' : null) : answerProblem(fd, answers[fd.id])]));
  const anyProblem = [...problems.values()].some(Boolean) || (v.requiresLegalAttestation && !attested);
  const busy = submit.isPending || uploading !== null;
  const setAnswer = (fid: string, value: AnswerValue | undefined) => setAnswers((c) => ({ ...c, [fid]: value }));

  async function send(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setFailure(null);
    if (anyProblem) return;
    try {
      const responses: { fieldId: string; value: AnswerValue }[] = [];
      for (const fd of v.fields) {
        let value = answers[fd.id];
        if (fd.type === 'fileUpload') {
          const file = files[fd.id];
          if (file) {
            setUploading(fd.label);
            value = await uploadAnswerFile(orgId!, uid!, assignment!.id, file);
          }
        }
        if (value === undefined || value === '') continue;
        responses.push({ fieldId: fd.id, value });
      }
      setUploading(null);
      await submit.mutateAsync({ assignmentId: assignment!.id, responses });
      try { window.sessionStorage.setItem(SUBMITTED_KEY, `"${v.title}" signed and recorded.`); } catch { /* private window */ }
      navigate('/forms/mine');
    } catch (err) {
      setUploading(null);
      setFailure(isCallFailure(err) ? err.message : err instanceof Error ? err.message : 'The form could not be submitted.');
    }
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          <p className={styles.eyebrow}>Form · version {v.versionNumber} · due {formatDay(assignment.dueDate)}</p>
          <div className={styles.titleLine}>
            <BackLink to="/forms/mine" label="Back to your forms" />
            <h1 className={styles.title}>{v.title}</h1>
          </div>
          {v.description && <p className={styles.subtitle}>{v.description}</p>}
        </div>
      </header>
      <div className={styles.body}>
        {v.instructions && <div className={`${styles.notice} ${styles.noticeInfo}`}><p className={own.prose} style={{ margin: 0 }}>{v.instructions}</p></div>}
        <form className={form.form} onSubmit={(e) => void send(e)} noValidate style={{ marginTop: 0, maxWidth: 680 }}>
          {failure && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{failure}</div>}
          {v.fields.map((fd, i) => (
            <Question key={fd.id} field={fd} index={i} value={answers[fd.id]} onChange={(val) => setAnswer(fd.id, val)}
              file={files[fd.id] ?? null} onFile={(file) => setFiles((c) => ({ ...c, [fd.id]: file }))}
              problem={touched ? problems.get(fd.id) ?? null : null} idPrefix={id(fd.id)} disabled={busy} />
          ))}
          {v.requiresLegalAttestation && (
            <div className={form.field}>
              <p className={form.label}>Attestation</p>
              <p className={own.statement}>{v.legalAttestationText}</p>
              <label className={form.check}>
                <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} aria-invalid={(touched && !attested) || undefined} aria-describedby={id('att-hint')} />
                <span>I have read the statement above and attest to it.</span>
              </label>
              <p className={touched && !attested ? form.error : form.hint} id={id('att-hint')}>{touched && !attested ? 'Tick to attest.' : `Recorded with your name, ${formatDay(today)}.`}</p>
            </div>
          )}
          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{uploading ? `Uploading ${uploading}…` : submit.isPending ? 'Submitting…' : 'Sign and submit'}</button>
            <Link to="/forms/mine" className={styles.btn}>Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
}

function Question({ field: fd, index, value, onChange, file, onFile, problem, idPrefix, disabled }: {
  field: FieldDef; index: number; value: AnswerValue | undefined; onChange: (v: AnswerValue | undefined) => void;
  file: File | null; onFile: (f: File | null) => void; problem: string | null; idPrefix: string; disabled: boolean;
}) {
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const inputId = `${idPrefix}-in`;
  const hintId = `${idPrefix}-hint`;
  const label = <>{index + 1}. {fd.label}{fd.required && <span aria-hidden="true"> *</span>}</>;
  const describe = [fd.description || fd.helpText ? `${idPrefix}-desc` : null, hintId].filter(Boolean).join(' ');
  const invalid = Boolean(problem) || undefined;
  const str = typeof value === 'string' ? value : '';
  const maxLen = Math.min(DESCRIPTION_MAX, fd.validationRules?.maxLength ?? DESCRIPTION_MAX);

  const body = (() => {
    switch (fd.type) {
      case 'shortText':
      case 'signature':
        return (<>
          <input id={inputId} className={`${form.input} ${fd.type === 'signature' ? own.signature : ''}`} type="text" value={str} maxLength={maxLen}
            onChange={(e) => onChange(e.target.value.slice(0, maxLen))} aria-invalid={invalid} aria-describedby={describe} disabled={disabled}
            placeholder={fd.type === 'signature' ? 'Type your full legal name' : undefined} autoComplete={fd.type === 'signature' ? 'name' : 'off'} />
          <CharCount value={str} max={maxLen} />
        </>);
      case 'longText':
        return (<>
          <textarea id={inputId} className={form.textarea} rows={4} value={str} maxLength={maxLen} onChange={(e) => onChange(e.target.value.slice(0, maxLen))} aria-invalid={invalid} aria-describedby={describe} disabled={disabled} />
          <CharCount value={str} max={maxLen} />
        </>);
      case 'number':
        return <input id={inputId} className={form.input} type="number" value={typeof value === 'number' ? value : ''} min={fd.validationRules?.minValue} max={fd.validationRules?.maxValue}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} aria-invalid={invalid} aria-describedby={describe} disabled={disabled} style={{ maxWidth: 220 }} />;
      case 'yesNo':
        return (
          <div className={own.yesNo} role="radiogroup" aria-labelledby={`${idPrefix}-l`} aria-describedby={describe}>
            {[['Yes', true], ['No', false]].map(([txt, val]) => (
              <label key={String(val)}><input type="radio" name={inputId} checked={value === val} onChange={() => onChange(val as boolean)} disabled={disabled} />{txt as string}</label>
            ))}
          </div>
        );
      case 'checkbox':
      case 'acknowledgementCheckbox':
        return <label className={form.check}><input id={inputId} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} aria-invalid={invalid} aria-describedby={describe} disabled={disabled} /><span>{fd.type === 'acknowledgementCheckbox' ? 'I have read and understood.' : 'Yes'}</span></label>;
      case 'radio':
        return (
          <ul className={own.radios} role="radiogroup" aria-labelledby={`${idPrefix}-l`} aria-describedby={describe}>
            {fd.options.map((o) => <li key={o}><label><input type="radio" name={inputId} checked={value === o} onChange={() => onChange(o)} disabled={disabled} />{o}</label></li>)}
          </ul>
        );
      case 'dropdown':
        return (
          <select id={inputId} className={form.select} value={str} onChange={(e) => onChange(e.target.value || undefined)} aria-invalid={invalid} aria-describedby={describe} disabled={disabled}>
            <option value="">Choose…</option>
            {fd.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'date':
        return <input id={inputId} className={form.input} type="date" value={str} onChange={(e) => onChange(e.target.value || undefined)} aria-invalid={invalid} aria-describedby={describe} disabled={disabled} style={{ maxWidth: 220 }} />;
      case 'fileUpload':
        return (<>
          <FileDrop id={inputId} accept={ANSWER_ACCEPT} prompt="Choose a file" describedBy={describe} invalid={invalid || Boolean(fileProblem)} disabled={disabled}
            onFile={(f) => { const why = answerFileProblem(f); setFileProblem(why); onFile(why ? null : f); }} />
          {fileProblem && <p className={form.error} role="alert">{fileProblem}</p>}
          {file && <p className={form.hint}>Selected: <b>{file.name}</b> ({formatBytes(file.size)})</p>}
        </>);
    }
  })();

  const usesLabelFor = ['shortText', 'signature', 'longText', 'number', 'dropdown', 'date', 'fileUpload', 'checkbox', 'acknowledgementCheckbox'].includes(fd.type);
  return (
    <div className={own.question}>
      {usesLabelFor ? <label className={own.qLabel} htmlFor={inputId}>{label}</label> : <span className={own.qLabel} id={`${idPrefix}-l`}>{label}</span>}
      {(fd.description || fd.helpText) && <p className={own.qDesc} id={`${idPrefix}-desc`}>{fd.description}{fd.description && fd.helpText ? ' ' : ''}{fd.helpText}</p>}
      {body}
      <p className={problem ? form.error : form.hint} id={hintId}>{problem ?? (fd.required ? 'Required.' : 'Optional.')}</p>
    </div>
  );
}
